import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsWidget = ({ alerts = [], loading = false, onDismissAlert }) => {
  const [expandedAlert, setExpandedAlert] = useState(null);
  const navigate = useNavigate();

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'info':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return 'AlertTriangle';
      case 'warning':
        return 'AlertCircle';
      case 'info':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'critical':
        return 'Crítico';
      case 'warning':
        return 'Atenção';
      case 'info':
        return 'Informação';
      default:
        return 'Alerta';
    }
  };

  const handleAlertAction = (alert) => {
    switch (alert?.actionType) {
      case 'navigate':
        navigate(alert?.actionPath);
        break;
      case 'approve':
        // Handle approval action
        console.log('Approve action for alert:', alert?.id);
        break;
      case 'assign': navigate('/service-orders');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="w-32 h-6 bg-muted rounded animate-pulse mb-4"></div>
        <div className="space-y-3">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-muted rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-muted rounded"></div>
                <div className="w-1/2 h-3 bg-muted rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const criticalAlerts = alerts?.filter(alert => alert?.severity === 'critical');
  const otherAlerts = alerts?.filter(alert => alert?.severity !== 'critical');

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Alertas do Sistema</h3>
          <div className="flex items-center space-x-2">
            {criticalAlerts?.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error border border-error/20">
                <Icon name="AlertTriangle" size={12} className="mr-1" />
                {criticalAlerts?.length} críticos
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {alerts?.length} total
            </span>
          </div>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {alerts?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success opacity-50" />
            <p className="text-muted-foreground">Nenhum alerta ativo</p>
            <p className="text-sm text-muted-foreground mt-1">
              Todos os sistemas estão funcionando normalmente
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-3">
            {/* Critical Alerts First */}
            {criticalAlerts?.map((alert) => (
              <div 
                key={alert?.id}
                className={`border rounded-lg p-4 ${getAlertColor(alert?.severity)} animate-pulse-slow`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Icon name={getAlertIcon(alert?.severity)} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm">{alert?.title}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium">
                          {getSeverityLabel(alert?.severity)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="X"
                          onClick={() => onDismissAlert(alert?.id)}
                          className="h-6 w-6 p-0"
                        />
                      </div>
                    </div>
                    <p className="text-sm mb-3">{alert?.message}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs opacity-75">{alert?.timestamp}</span>
                      {alert?.actionLabel && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAlertAction(alert)}
                        >
                          {alert?.actionLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Other Alerts */}
            {otherAlerts?.map((alert) => (
              <div 
                key={alert?.id}
                className={`border rounded-lg p-4 hover:bg-muted/30 transition-colors ${getAlertColor(alert?.severity)}`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Icon name={getAlertIcon(alert?.severity)} size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm">{alert?.title}</h4>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium">
                          {getSeverityLabel(alert?.severity)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName={expandedAlert === alert?.id ? "ChevronUp" : "ChevronDown"}
                          onClick={() => setExpandedAlert(expandedAlert === alert?.id ? null : alert?.id)}
                          className="h-6 w-6 p-0"
                        />
                      </div>
                    </div>
                    
                    <p className={`text-sm ${expandedAlert === alert?.id ? '' : 'line-clamp-1'}`}>
                      {alert?.message}
                    </p>
                    
                    {expandedAlert === alert?.id && (
                      <div className="mt-3 pt-3 border-t border-current/20">
                        <div className="flex items-center justify-between">
                          <span className="text-xs opacity-75">{alert?.timestamp}</span>
                          <div className="flex items-center space-x-2">
                            {alert?.actionLabel && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAlertAction(alert)}
                              >
                                {alert?.actionLabel}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="X"
                              onClick={() => onDismissAlert(alert?.id)}
                            >
                              Dispensar
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsWidget;